/**
 * ============================================================================
 * [P3-01] DualPanelView - 듀얼 패널 뷰 컨테이너
 *
 * 기능:
 * - 단일/듀얼 모드 분기 (isDualMode)
 * - 듀얼 모드: CSS Grid 1fr 4px 1fr + DualDivider
 * - 모바일: 탭 전환 (activeMobilePanel)
 * - 각 패널 mode 상태 관리 (panel1Mode, panel2Mode)
 *
 * [DP-01] 듀얼 패널 고정 기능
 * [2026-03-02] 신규 생성
 * ============================================================================
 */

// ============================================================================
// 1. Imports
// ============================================================================
import { useState, useCallback } from 'react'
import { ScriptDetailPanel } from './ScriptDetailPanel'
import type { Script, PanelIndex, DetailPanelMode } from '@/types'
import styles from './DualPanelView.module.css'

// ============================================================================
// 2. Types
// ============================================================================
interface DualPanelViewProps {
  panel1Script: Script
  panel2Script: Script | null        // null이면 단일 모드
  onClosePanel: (panelIndex: PanelIndex) => void
  onDeleteScript: (id: string) => void
  onOpenInEditor?: (script: Script) => void  // 전체 에디터에서 열기
}

// ============================================================================
// 3. DualPanelView Component
// ============================================================================
export function DualPanelView({
  panel1Script,
  panel2Script,
  onClosePanel,
  onDeleteScript,
  onOpenInEditor,
}: DualPanelViewProps) {
  // ========================================================================
  // 3-1. 패널 모드 상태 (read / edit)
  // ========================================================================
  const [panel1Mode, setPanel1Mode] = useState<DetailPanelMode>('read')
  const [panel2Mode, setPanel2Mode] = useState<DetailPanelMode>('read')

  // ========================================================================
  // 3-2. 모바일 활성 패널 (듀얼 모드에서만 사용)
  // ========================================================================
  const [activeMobilePanel, setActiveMobilePanel] = useState<PanelIndex>(0)

  // ========================================================================
  // 3-3. 듀얼 모드 판별
  // ========================================================================
  const isDualMode = panel2Script !== null

  // ========================================================================
  // 3-4. 패널 닫기 핸들러
  // ========================================================================
  const handleClosePanel1 = useCallback(() => {
    setPanel1Mode('read')
    onClosePanel(0)
  }, [onClosePanel])

  const handleClosePanel2 = useCallback(() => {
    setPanel2Mode('read')
    onClosePanel(1)
  }, [onClosePanel])

  // ========================================================================
  // 3-5. 패널 모드 변경 핸들러
  // ========================================================================
  const handlePanel1ModeChange = useCallback((mode: DetailPanelMode) => {
    setPanel1Mode(mode)
  }, [])

  const handlePanel2ModeChange = useCallback((mode: DetailPanelMode) => {
    setPanel2Mode(mode)
  }, [])

  // ========================================================================
  // 3-6. CSS 클래스
  // ========================================================================
  const containerClassName = `${styles.dualPanelView}${isDualMode ? ` ${styles.dualMode}` : ''}`

  // ========================================================================
  // 3-7. 단일 모드 렌더링
  // ========================================================================
  if (!isDualMode) {
    return (
      <div className={containerClassName}>
        <ScriptDetailPanel
          script={panel1Script}
          panelIndex={0}
          mode={panel1Mode}
          onModeChange={handlePanel1ModeChange}
          onClose={handleClosePanel1}
          onDelete={onDeleteScript}
          onOpenInEditor={onOpenInEditor}
        />
      </div>
    )
  }

  // ========================================================================
  // 3-8. 듀얼 모드 렌더링
  // ========================================================================
  return (
    <div className={containerClassName}>
      {/* 모바일 탭 전환 */}
      <div className={styles.mobilePanelTabs}>
        <button
          className={`${styles.mobilePanelTab}${activeMobilePanel === 0 ? ` ${styles.active}` : ''}`}
          onClick={() => setActiveMobilePanel(0)}
        >
          📌 패널 1: {panel1Script.name || '이름 없음'}
        </button>
        <button
          className={`${styles.mobilePanelTab}${activeMobilePanel === 1 ? ` ${styles.active}` : ''}`}
          onClick={() => setActiveMobilePanel(1)}
        >
          📌 패널 2: {panel2Script.name || '이름 없음'}
        </button>
      </div>

      {/* 패널 1 */}
      <ScriptDetailPanel
        script={panel1Script}
        panelIndex={0}
        mode={panel1Mode}
        onModeChange={handlePanel1ModeChange}
        onClose={handleClosePanel1}
        onDelete={onDeleteScript}
        onOpenInEditor={onOpenInEditor}
        mobileHidden={activeMobilePanel !== 0}
      />

      {/* 구분선 */}
      <div className={styles.dualDivider} />

      {/* 패널 2 */}
      <ScriptDetailPanel
        script={panel2Script}
        panelIndex={1}
        mode={panel2Mode}
        onModeChange={handlePanel2ModeChange}
        onClose={handleClosePanel2}
        onDelete={onDeleteScript}
        onOpenInEditor={onOpenInEditor}
        mobileHidden={activeMobilePanel !== 1}
      />
    </div>
  )
}

export default DualPanelView
